const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const TOKEN_KEY = 'market_commentary_token';

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    options?: RequestInit
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${path}`;
    const token = this.getToken();

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options?.headers,
    };

    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
        ...options,
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP error ${response.status}`,
        };
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  async get<T>(path: string): Promise<ApiResponse<T>> {
    return this.request<T>('GET', path);
  }

  async post<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>('POST', path, body);
  }

  async put<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', path, body);
  }

  async delete<T>(path: string): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', path);
  }

  // Upload file with progress tracking
  async uploadFile(
    path: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<{ id: number; upload_url: string; r2_key: string }>> {
    // First, request an upload URL
    const initResponse = await this.post<{
      id: number;
      upload_url: string;
      r2_key: string;
      expires_at: string;
    }>(path, {
      filename: file.name,
      mimetype: file.type,
      size: file.size,
    });

    if (!initResponse.success || !initResponse.data) {
      return initResponse as ApiResponse<{ id: number; upload_url: string; r2_key: string }>;
    }

    // For now, we'll use a simple fetch for upload
    // In production, you might want XMLHttpRequest for progress
    try {
      const uploadUrl = initResponse.data.upload_url;

      // If it's a relative URL, prepend the base URL
      const fullUploadUrl = uploadUrl.startsWith('/')
        ? `${this.baseUrl}${uploadUrl}`
        : uploadUrl;

      // Upload the file
      const uploadResponse = await fetch(fullUploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        return {
          success: false,
          error: 'Failed to upload file',
        };
      }

      // Confirm the upload
      const confirmResponse = await this.post(`/documents/${initResponse.data.id}/confirm`);

      if (!confirmResponse.success) {
        return {
          success: false,
          error: confirmResponse.error || 'Failed to confirm upload',
        };
      }

      if (onProgress) {
        onProgress(100);
      }

      return {
        success: true,
        data: initResponse.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed',
      };
    }
  }
}

export const api = new ApiClient(API_BASE_URL);
